// A shelf's count against its Critical, Min and Recommended ticks, coloured
// red, amber or green, with days of draw. Small-parts buckets counted as
// "enough" get an enough / not-enough pill instead of a track.
import type { ShelfRow } from "../lib/types";
import { drawPhrase } from "../lib/copy";

export interface ShelfGaugeProps {
  shelf: ShelfRow;
  variant?: "card" | "compact" | "board";
  /** Show "filled by / drawn by". */
  showFlow?: boolean;
}

// contract.ts is getting `jiraState?: string | null` on ShelfRow; read it defensively until then.
type WithJira = ShelfRow & { jiraState?: string | null };

export function ShelfGauge({ shelf, variant = "card", showFlow }: ShelfGaugeProps) {
  const { level, critical, min, recommended } = shelf;
  const jira = (shelf as WithJira).jiraState;
  const jiraTag = jira && variant !== "compact" ? <span className="gauge__jira" title="State in Jira">Jira: {jira}</span> : null;
  const cls = ["gauge", `gauge--${shelf.state}`, variant === "board" ? "gauge--board" : ""].filter(Boolean).join(" ");
  const label = (
    <span className="gauge__label"><span className="gauge__code">{shelf.code}</span>{shelf.name}</span>
  );

  if (shelf.countMode === "enough") {
    const word = level.enough == null ? "Not counted" : level.enough ? "Enough" : "Not enough";
    const tone = level.enough == null ? "pill--muted" : level.enough ? "pill--g" : "pill--r";
    return (
      <div className={cls} data-testid="shelf-gauge" aria-label={`${shelf.name}: ${word.toLowerCase()}`}>
        {label}
        <span className={`pill gauge__enough ${tone}`}>{word}</span>
        {variant !== "compact" && showFlow ? <span className="gauge__meta">{flow(shelf)}</span> : null}
        {jiraTag}
      </div>
    );
  }

  const qty = level.qty;
  // The track runs to a quarter past Recommended (or the count, if higher), so
  // Recommended never sits on the end and a full shelf still has room.
  const top = Math.max((recommended ?? min ?? critical ?? 100) * 1.25, (qty ?? 0) * 1.05, 1);
  const at = (n: number) => `${Math.min(100, (n / top) * 100)}%`;
  const draw = drawPhrase(shelf.daysOfDraw);
  const meta = qty == null ? "Never counted" : critical != null && qty < critical ? "Below Critical: fill it today" : draw ?? "";
  const aria = qty == null ? `${shelf.name}: never counted` : `${shelf.name} at ${qty}${meta ? `, ${meta}` : ""}`;

  return (
    <div className={cls} data-testid="shelf-gauge" data-state={shelf.state} aria-label={aria}>
      <div className="gauge__head">
        {label}
        <span className="gauge__value">{qty ?? "–"}</span>
      </div>
      <div className="gauge__track" aria-hidden="true">
        {qty != null ? <span className="gauge__fill" style={{ width: at(qty) }} /> : null}
        {critical != null ? <span className="gauge__tick gauge__tick--critical" style={{ left: at(critical) }} title={`Critical ${critical}`} /> : null}
        {min != null ? <span className="gauge__tick gauge__tick--min" style={{ left: at(min) }} title={`Min ${min}`} /> : null}
        {recommended != null ? <span className="gauge__tick gauge__tick--recommended" style={{ left: at(recommended) }} title={`Recommended ${recommended}`} /> : null}
        {qty != null ? <span className="gauge__marker" style={{ left: at(qty) }} /> : null}
      </div>
      {variant === "card" ? (
        <div className="gauge__ticks" aria-hidden="true">
          {critical != null ? <span style={{ left: at(critical) }}>{critical}</span> : null}
          {min != null ? <span style={{ left: at(min) }}>{min}</span> : null}
          {recommended != null ? <span style={{ left: at(recommended) }}>{recommended}</span> : null}
        </div>
      ) : null}
      {variant !== "compact" ? <span className="gauge__meta">{meta}{showFlow ? ` · ${flow(shelf)}` : ""}</span> : null}
      {jiraTag}
    </div>
  );
}

function flow(s: ShelfRow) {
  return [s.filledBy ? `filled by ${s.filledBy}` : null, s.drawnBy ? `drawn by ${s.drawnBy}` : null].filter(Boolean).join(", ") || "not planned";
}
